import { createContext, useCallback, useContext, useRef, useState } from "react";

/**
 * Minimal "dirty form" guard, decoupled from React Router data routers.
 * - A form registers a guard with `setGuard(isDirty, onConfirm)` whenever its dirty state changes.
 * - Navigation triggers (sidebar links, header buttons) call `requestNavigate(action)`:
 *     - if no dirty form is active, the action runs immediately
 *     - otherwise the pending action is queued and the registered form is asked to
 *       confirm via the `pending` value exposed to the form.
 */
const Ctx = createContext({
  setGuard: () => {},
  requestNavigate: (fn) => fn(),
  pending: null,
  proceed: () => {},
  cancel: () => {},
});

export function NavigationGuardProvider({ children }) {
  const guardRef = useRef(null); // { isDirty: bool }
  const [pending, setPending] = useState(null); // pending fn to run on proceed

  const setGuard = useCallback((isDirty) => {
    guardRef.current = { isDirty };
  }, []);

  const requestNavigate = useCallback((fn) => {
    if (guardRef.current?.isDirty) {
      setPending(() => fn);
    } else {
      fn();
    }
  }, []);

  const proceed = useCallback(() => {
    const fn = pending;
    setPending(null);
    // Once the user discards, clear the guard so subsequent nav doesn't re-prompt
    guardRef.current = null;
    if (fn) fn();
  }, [pending]);

  const cancel = useCallback(() => setPending(null), []);

  return (
    <Ctx.Provider value={{ setGuard, requestNavigate, pending, proceed, cancel }}>
      {children}
    </Ctx.Provider>
  );
}

export const useNavigationGuard = () => useContext(Ctx);
